import React, { useEffect } from 'react';
import { getElementStyleMathValue } from '@gp/utils/common';
import {
  FIXED_HEADER_HEIGHT,
  getProjectDetailsHeaderHeight,
} from '@gp/pages/Pipeline/ProjectDetails/utils';

import type { IMilestone } from '@gp/pages/Pipeline/ProjectDetails/Milestone/types';

// 不需要滚动的milestone索引
const SPECIAL_INDEXES = [-1, 0];

const getOthersDistance = (): number => {
  // project详情页顶层节点
  const detailsPageNode = document.querySelector('.dbg-project-details');
  const detailsPageTeaBodyNode = detailsPageNode?.querySelector(
    '.tea-layout__content-body',
  );
  // project详情页milestone页签tab顶层节点
  const milestoneTabPageNode = detailsPageNode?.querySelector(
    '.dbg-project-details-milestone',
  );
  // project详情页milestone页签 筛选器工具栏节点
  const milestoneTabPageToolbarNode = milestoneTabPageNode?.querySelector(
    '.dbg-milestone-justify',
  );

  const headerNodeHeight = getProjectDetailsHeaderHeight(detailsPageNode);
  const detailsPageTeaBodyNodePaddingTop = getElementStyleMathValue(
    detailsPageTeaBodyNode as any,
    'padding-top',
  );
  const milestoneTabPageToolbarNodeHeight = getElementStyleMathValue(
    milestoneTabPageToolbarNode as any,
    'height',
  );
  const milestoneTabPageToolbarNodeMarginTop = getElementStyleMathValue(
    milestoneTabPageToolbarNode as any,
    'margin-top',
  );
  const milestoneTabPageToolbarNodeMarginBottom = getElementStyleMathValue(
    milestoneTabPageToolbarNode as any,
    'margin-bottom',
  );

  return (
    headerNodeHeight +
    detailsPageTeaBodyNodePaddingTop +
    milestoneTabPageToolbarNodeHeight +
    milestoneTabPageToolbarNodeMarginTop +
    milestoneTabPageToolbarNodeMarginBottom
  );
};

/**
 * 获取当前milestone卡片节点之前的milestones卡片节点的总高度
 * @param milestoneId 当前milestone的id
 * @param milestones milestones集合
 * @param nodes milestones卡片节点集合
 * @returns 当前milestone卡片节点之前的milestones卡片节点的总高度
 */
const getMilestoneDistance = (
  milestoneId: string,
  milestones: IMilestone[],
  nodes: HTMLCollection,
): number => {
  // 当前milestone的索引
  const milestoneIdx = milestones.findIndex(item => {
    const { milestone_id: itemMilestoneId } = item || {};
    return itemMilestoneId === milestoneId;
  });

  if (SPECIAL_INDEXES.includes(milestoneIdx)) return 0;

  // 当前milestone滚动的顶端时，需要滚动的距离
  let distance = 0;

  /**
   * 累加milestoneIdx索引之前的milestone卡片节点的高度
   */
  for (let i = 0; i < nodes.length; i++) {
    if (i === milestoneIdx) break;
    const node = nodes[i];
    const marginTop = getElementStyleMathValue(node, 'margin-top');
    const height = getElementStyleMathValue(node, 'height');
    distance = distance + marginTop + height;
  }

  return distance;
};

export default function useMilestoneAutoScroll(
  pageContainer?: React.RefObject<HTMLElement | null>,
  milestoneListNode?: React.RefObject<HTMLElement>,
  props?: {
    milestoneId?: string;
    milestones?: IMilestone[] | null;
    deps?: React.DependencyList;
  },
  isClickMilestoneId?: boolean,
) {
  const { milestoneId, milestones, deps } = props || {};

  useEffect(() => {
    if (!Array.isArray(milestones) || !milestones.length) return;
    if (!milestoneId) return;
    if (!milestoneListNode?.current?.children) return;

    const milestoneDistance = getMilestoneDistance(
      milestoneId,
      milestones,
      milestoneListNode.current.children,
    );
    const othersDistance = getOthersDistance();
    const scrollDistance =
      milestoneDistance === 0
        ? 0
        : milestoneDistance + othersDistance - FIXED_HEADER_HEIGHT;

    if (pageContainer?.current && scrollDistance > 0) {
      pageContainer?.current.scrollTo({
        top: scrollDistance,
        behavior: 'smooth',
      });
    }
  }, [deps, isClickMilestoneId]);
}
